import { useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';

/** How long each line stays before the next takes its place. */
const LINE_MS = 2200;

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

/** On the page for a screen reader, off it for everyone else. In px: MUI reads 1 as 100%. */
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

export interface ReadingLineProps {
  /** Said in turn while the wait lasts; the last one stays. */
  lines: string[];
}

/**
 * ReadingLine says what is happening while what they said is read: a line on
 * Keni's side rather than a bubble, so it reads as work going on rather than
 * something said, with dots that keep moving so an unchanged line still says
 * it is working. Said once to a screen reader.
 */
export const ReadingLine = ({ lines }: ReadingLineProps) => {
  const [at, setAt] = useState(0);

  useEffect(() => {
    if (at >= lines.length - 1) return;
    const timer = setTimeout(() => setAt((i) => i + 1), LINE_MS);
    return () => clearTimeout(timer);
  }, [at, lines.length]);

  return (
    <Box sx={{ maxWidth: '82%' }}>
      <Box sx={visuallyHidden} role='status'>
        {lines[0]}
      </Box>
      <Typography
        key={at}
        aria-hidden
        variant='body1'
        // Neutral 600 rather than text.secondary, which fails contrast here.
        sx={{
          color: 'custom.neutral.600',
          fontStyle: 'italic',
          '@keyframes readingIn': { from: { opacity: 0 }, to: { opacity: 1 } },
          animation: 'readingIn .4s ease both',
          [REDUCED_MOTION]: { animation: 'none' },
        }}
      >
        {lines[Math.min(at, lines.length - 1)]}
        {[0, 1, 2].map((dot) => (
          <Box
            key={dot}
            component='span'
            sx={{
              '@keyframes readingDot': {
                '0%, 60%, 100%': { opacity: 0.2 },
                '30%': { opacity: 1 },
              },
              animation: 'readingDot 1.2s ease-in-out infinite',
              animationDelay: `${dot * 0.2}s`,
              [REDUCED_MOTION]: { animation: 'none' },
            }}
          >
            .
          </Box>
        ))}
      </Typography>
    </Box>
  );
};

ReadingLine.displayName = 'ReadingLine';
export default ReadingLine;
